import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import WhatsAppSessionManager from '../WhatsAppSessionManager';
import { useAuth } from '../../contexts/AuthContext';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

/**
 * TabAccounts - WhatsApp Personal Accounts Management
 * ToS-safe tips & account linking
 * RBAC: Admin/Management full, Supervisor read-only
 */
const TabAccounts = () => {
  const { isAdmin, isManagement, isSupervisor } = useAuth();
  const [serviceHealth, setServiceHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [showLinkingUI, setShowLinkingUI] = useState(false);

  const canEdit = isAdmin() || isManagement();
  const isReadOnly = isSupervisor() && !canEdit;

  useEffect(() => {
    checkServiceHealth();
  }, []);

  const checkServiceHealth = async (showError = false) => {
    setHealthLoading(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(
        `${BACKEND_URL}/api/whatsapp/health`,
        { 
          signal: controller.signal,
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setServiceHealth(data);
      } else {
        const errorData = await response.json().catch(() => ({ status: 'unavailable' }));
        setServiceHealth({ 
          status: 'unavailable', 
          message: errorData.message || 'Service unavailable',
          silent: !showError 
        });
      }
    } catch (error) {
      console.error('WhatsApp health check error:', error);
      setServiceHealth({ 
        status: 'unavailable', 
        message: error.name === 'AbortError' ? 'Connection timeout' : 'Service unavailable',
        silent: !showError 
      });
    } finally {
      setHealthLoading(false);
    }
  };

  const handleLinkAccount = async () => {
    if (serviceHealth?.status === 'unavailable') {
      // Try health check again before showing error
      await checkServiceHealth(true);
      if (serviceHealth?.status === 'unavailable') {
        alert('WhatsApp service is currently unavailable. Please try again later or contact your administrator.');
        return;
      }
    }
    setShowLinkingUI(true);
  };

  const handleRetry = () => {
    checkServiceHealth(true);
  };

  return (
    <div 
      className="p-6 overflow-y-auto"
      style={{
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          WhatsApp Accounts (Personal)
        </h2>
        <p className="text-sm text-gray-600">
          ToS-safe tips & usage for personal WhatsApp accounts
        </p>
      </div>

      {/* Service Health - Show if unavailable and user interacted */}
      {serviceHealth?.status === 'unavailable' && !serviceHealth?.silent && !healthLoading && (
        <Alert className="mb-6 bg-yellow-50 border-yellow-200">
          <AlertDescription>
            <div className="flex items-start gap-3">
              <span className="text-xl">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-yellow-900 mb-2">
                  WhatsApp Service Unavailable
                </p>
                <p className="text-sm text-yellow-800 mb-3">
                  {serviceHealth?.message || 'The WhatsApp personal service is not currently running. This feature requires the WhatsApp service to be configured and started.'}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRetry}
                    size="sm"
                    variant="outline"
                    className="bg-yellow-100 text-yellow-900 border-yellow-300 hover:bg-yellow-200"
                  >
                    🔄 Retry Connection
                  </Button>
                  <Button
                    onClick={() => window.open('/communications?tab=broadcast', '_self')}
                    size="sm"
                    variant="outline"
                    className="text-yellow-900 border-yellow-300 hover:bg-yellow-100"
                  >
                    Use Broadcast Mode Instead
                  </Button>
                </div>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* ToS Warning Banner */}
      <Card className="mb-6 bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-1">
                WhatsApp Terms of Service Reminder
              </h3>
              <p className="text-sm text-amber-800 mb-2">
                Using personal WhatsApp accounts for bulk messaging may violate WhatsApp's Terms of Service. 
                Our <strong>Broadcast Mode</strong> provides a ToS-compliant alternative for team communications.
              </p>
              <p className="text-xs text-amber-700">
                💡 Tip: Use WhatsApp Broadcast Lists (manual) for compliant group messaging.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Read-Only Notice for Supervisors */}
      {isReadOnly && (
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <AlertDescription className="text-blue-900">
            📖 You have read-only access to WhatsApp accounts. Contact an Admin or Manager to link accounts.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      {healthLoading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">Checking WhatsApp service status...</p>
            <p className="text-xs text-gray-400 mt-2">This may take a few seconds</p>
          </CardContent>
        </Card>
      ) : serviceHealth?.status === 'unavailable' && serviceHealth?.silent ? (
        /* Service Unavailable - Show friendly message */
        <Card className="border-yellow-200">
          <CardContent className="py-12 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">⚠️</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                WhatsApp Personal Service Not Available
              </h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                The WhatsApp personal linking service is not currently running. 
                You can still use our ToS-compliant <strong>Broadcast Mode</strong> for sending messages.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                onClick={handleRetry}
                variant="outline"
                className="border-yellow-300 text-yellow-900 hover:bg-yellow-50"
              >
                🔄 Retry Connection
              </Button>
              <Button
                onClick={() => window.open('/communications?tab=broadcast', '_self')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Use Broadcast Mode →
              </Button>
            </div>

            <details className="text-left max-w-md mx-auto">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                Technical Details
              </summary>
              <div className="mt-2 p-3 bg-gray-50 rounded text-xs text-gray-600">
                <p><strong>Status:</strong> Service Unavailable</p>
                <p><strong>Endpoint:</strong> /api/whatsapp/health</p>
                <p><strong>Reason:</strong> {serviceHealth?.message || 'WhatsApp personal service is not running'}</p>
                <p className="mt-2">Contact your administrator to enable personal WhatsApp linking.</p>
              </div>
            </details>
          </CardContent>
        </Card>
      ) : showLinkingUI || serviceHealth?.status === 'healthy' || serviceHealth?.success ? (
        <div>
          <WhatsAppSessionManager readOnly={isReadOnly} />
        </div>
      ) : (
        /* Empty State - Service Available */
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">💬</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No WhatsApp Accounts Linked Yet
              </h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                Link your personal WhatsApp account to use advanced features. 
                Remember to follow WhatsApp's Terms of Service when sending messages.
              </p>
            </div>

            {canEdit && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleLinkAccount}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  + Link My WhatsApp
                </Button>
                <Button
                  onClick={() => window.open('/communications?tab=broadcast', '_self')}
                  variant="outline"
                >
                  Learn about Broadcast Mode (ToS-safe)
                </Button>
              </div>
            )}

            {!canEdit && (
              <p className="text-sm text-gray-500 mt-4">
                Contact an Admin or Manager to link WhatsApp accounts
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Additional Resources */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-lg text-blue-900">✅ ToS-Compliant: Broadcast Mode</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-blue-800 mb-3">
              Use our Broadcast Mode to compose messages, generate deeplinks, and track engagement 
              without automated sending.
            </p>
            <Button
              onClick={() => window.open('/communications?tab=broadcast', '_self')}
              size="sm"
              variant="outline"
              className="text-blue-600 border-blue-600 hover:bg-blue-100"
            >
              Go to Broadcast Mode →
            </Button>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="text-lg text-purple-900">📝 Message Templates</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-purple-800 mb-3">
              Create reusable message templates with dynamic tokens for faster communications.
            </p>
            <Button
              onClick={() => window.open('/communications?tab=templates', '_self')}
              size="sm"
              variant="outline"
              className="text-purple-600 border-purple-600 hover:bg-purple-100"
            >
              Manage Templates →
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TabAccounts;
